<template>
  <v-dialog
    :value="value"
    :fullscreen="device.isMobile"
    max-width="480"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card v-if="order" class="meal-rating-dialog d-flex flex-column">
      <v-card-text class="pa-4 flex-grow-1">
        <div class="d-flex justify-end" v-if="!device.isMobile">
          <v-btn icon small @click="close"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- Obrok -->
        <div class="meal-box rounded-lg pa-3 mb-5 d-flex">
          <v-img
            v-if="imageUrl"
            :src="imageUrl"
            width="72"
            height="72"
            max-width="72"
            class="rounded-lg mr-3 flex-shrink-0 grey lighten-3"
            cover
          />
          <div class="flex-grow-1" style="min-width: 0">
            <div class="caption grey--text text--darken-1">
              {{ dayLabel }}<span v-if="shiftNumber"> · {{ $t("meal_rating.shift", { n: shiftNumber }) }}</span>
            </div>
            <div class="subtitle-1 font-weight-bold black--text meal-name">
              {{ translate(order.meals ? order.meals.name : "") }}
            </div>
            <div class="d-flex align-center">
              <v-rating
                v-model="form.rating"
                color="amber"
                background-color="grey lighten-1"
                hover
                dense
              />
              <span v-if="form.rating" class="ml-2 body-2 grey--text text--darken-1">
                {{ $t(`meal_rating.labels.${form.rating}`) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Šta nije bilo u redu -->
        <template v-if="showIssues">
          <div class="subtitle-1 font-weight-bold black--text">
            {{ $t("meal_rating.issues_title") }}
          </div>
          <div class="caption grey--text text--darken-1 mb-2">
            {{ $t("meal_rating.issues_hint") }}
          </div>
          <v-chip-group v-model="form.issues" multiple column class="mb-3">
            <v-chip
              v-for="code in issueCodes"
              :key="code"
              :value="code"
              outlined
              active-class="issue-chip--active primary--text"
              class="issue-chip"
            >
              {{ $t(`meal_rating.issues.${code}`) }}
            </v-chip>
          </v-chip-group>
        </template>

        <!-- Komentar -->
        <div class="subtitle-1 font-weight-bold black--text mb-1">
          {{ $t("meal_rating.comment_label") }}
          <span class="font-weight-regular grey--text">({{ $t("meal_rating.optional") }})</span>
        </div>
        <v-textarea
          v-model="form.comment"
          :placeholder="$t('meal_rating.comment_placeholder')"
          maxlength="1000"
          rows="3"
          auto-grow
          outlined
          hide-details
          background-color="white"
        />

        <div class="privacy-note rounded-lg pa-3 mt-4 d-flex align-center body-2">
          <v-icon small class="mr-2">mdi-lock-outline</v-icon>
          {{ $t("meal_rating.privacy_note") }}
        </div>

        <div v-if="order.rating_deadline" class="caption grey--text mt-3">
          {{ $t("meal_rating.deadline_until", { date: deadlineLabel }) }}
        </div>
      </v-card-text>

      <v-card-actions class="flex-column pa-4 pt-0">
        <v-btn
          block
          x-large
          depressed
          color="primary"
          class="rounded-lg text-none font-weight-bold"
          :disabled="!form.rating"
          :loading="saving"
          @click="save(false)"
        >
          {{ order.rating ? $t("meal_rating.update") : $t("meal_rating.submit") }}
        </v-btn>
        <v-btn
          v-if="!order.rating"
          text
          class="mt-2 ml-0 text-none"
          :disabled="!form.rating || saving"
          @click="save(true)"
        >
          {{ $t("meal_rating.skip_details") }}
        </v-btn>
        <v-btn v-else text class="mt-2 ml-0 text-none" @click="close">
          {{ $t("cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import { orderMealImageUrl } from "@/utils/mealImage";
import userService from "@/services/user";
import hasSnackbar from "@/mixins/hasSnackbar";

// Isti kodovi kao MealRating::ISSUES na backendu
const ISSUE_CODES = ["cold", "small_portion", "bad_taste", "too_salty", "late", "wrong_meal"];
// Razlozi se nude za ocene od 1 do 3
const ISSUES_MAX_RATING = 3;

export default {
  name: "MealRatingDialog",
  mixins: [hasSnackbar],
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
    // Početna ocena (npr. klik na zvezdicu u kartici na meniju)
    initialRating: { type: Number, default: 0 },
  },
  data: () => ({
    issueCodes: ISSUE_CODES,
    form: { rating: 0, issues: [], comment: "" },
    saving: false,
  }),
  computed: {
    imageUrl() {
      return orderMealImageUrl(this.order, "small");
    },
    showIssues() {
      return this.form.rating > 0 && this.form.rating <= ISSUES_MAX_RATING;
    },
    dayLabel() {
      if (!this.order?.date) return "";
      const weekday = this.localeDate(this.order.date, { weekday: "long" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${this.localeDate(this.order.date)}`;
    },
    shiftNumber() {
      return parseInt(this.order?.shift, 10) || null;
    },
    deadlineLabel() {
      return moment(this.order.rating_deadline).format("DD.MM.YYYY. HH:mm");
    },
  },
  watch: {
    value(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    resetForm() {
      const existing = this.order?.rating;
      this.form = {
        rating: this.initialRating || (existing ? existing.rating : 0),
        issues: existing?.issues ? [...existing.issues] : [],
        comment: existing?.comment || "",
      };
    },
    close() {
      this.$emit("input", false);
    },
    save(skipDetails) {
      const payload = {
        rating: this.form.rating,
        issues: !skipDetails && this.showIssues ? this.form.issues : [],
        comment: skipDetails ? null : this.form.comment || null,
      };
      this.saving = true;
      userService
        .rateOrderMeal(this.order.id, payload)
        .then((res) => {
          this.showSuccessSnackbar(this.$t("meal_rating.saved"));
          this.$emit("saved", res.data.data);
          this.close();
        })
        .catch((error) => {
          this.showErrorSnackbar(
            error?.response?.data?.message || this.$t("meal_rating.error_saving")
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.meal-rating-dialog {
  background: #f8f4f1 !important;
}
.meal-box {
  background: #fff;
}
.meal-name {
  line-height: 1.35;
}
.issue-chip {
  background: #fff !important;
  border-color: #ddd !important;
  font-weight: 500;
}
.issue-chip--active {
  border-color: currentColor !important;
}
.issue-chip--active::before {
  opacity: 0.08 !important;
}
.privacy-note {
  background: #eaf0f6;
  color: #4a5a6a;
}
</style>
